import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';

const AdminBrandingSettings = () => {
  const [settings, setSettings] = useState({
    centerName: '',
    tagline: '',
    primaryColor: '#667eea',
    logoUrl: '',
    upiId: '',
    upiQrUrl: ''
  });
  const [logoPreview, setLogoPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { token } = useAuth();

  useEffect(() => {
    fetchBrandingSettings();
  }, []);

  const fetchBrandingSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/admin/branding`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.settings) {
          setSettings({
            centerName: data.settings.centerName || '',
            tagline: data.settings.tagline || '',
            primaryColor: data.settings.primaryColor || '#667eea',
            logoUrl: data.settings.logoUrl || '',
            upiId: data.settings.upiId || '',
            upiQrUrl: data.settings.upiQrUrl || ''
          });
          if (data.settings.logoUrl) {
            setLogoPreview(data.settings.logoUrl);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching branding settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a PNG, JPG, SVG or WebP image');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Logo file size must be less than 2MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
      setSettings({ ...settings, logoUrl: reader.result });
    };
    reader.readAsDataURL(file);
    setError('');
  };

  const removeLogo = () => {
    setLogoPreview('');
    setSettings({ ...settings, logoUrl: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!settings.centerName.trim()) {
      setError('Sports center name is required');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const response = await fetch(`${API_URL}/admin/branding`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Branding settings saved successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Error saving branding settings:', err);
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    fetchBrandingSettings();
    setError('');
    setSuccess('');
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>Loading branding settings...</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {success && <div className="admin-alert success">{success}</div>}
      {error && <div className="admin-alert error">{error}</div>}

      <div className="admin-card">
        <div className="admin-card-header">Branding Settings</div>
        <div className="admin-card-body">
          <form onSubmit={handleSubmit}>
            {/* Logo Upload Section */}
            <div style={{ marginBottom: '24px' }}>
              <label className="admin-label">Logo</label>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginTop: '8px' }}>
                {/* Logo Preview */}
                <div style={{
                  width: '120px',
                  height: '120px',
                  border: '2px dashed #e5e7eb',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f9fafb',
                  overflow: 'hidden'
                }}>
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <svg width="40" height="40" fill="none" viewBox="0 0 24 24" stroke="#9ca3af">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  )}
                </div>

                {/* Upload Controls */}
                <div style={{ flex: 1 }}>
                  <input
                    type="file"
                    id="logo-upload"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    onChange={handleLogoUpload}
                    style={{ display: 'none' }}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <label
                      htmlFor="logo-upload"
                      className="admin-btn admin-btn-secondary"
                      style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    >
                      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Upload Logo
                    </label>
                    {logoPreview && (
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="admin-btn admin-btn-danger"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                      >
                        <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Remove
                      </button>
                    )}
                  </div>
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                    Recommended: Square image, 200x200px minimum. PNG, JPG, SVG or WebP. Max 2MB.
                  </p>
                </div>
              </div>
            </div>

            {/* Center Name */}
            <div className="admin-form-row" style={{ marginBottom: '16px' }}>
              <div className="admin-field" style={{ flex: 2 }}>
                <label className="admin-label">Sports Center Name *</label>
                <input
                  type="text"
                  value={settings.centerName}
                  onChange={(e) => setSettings({ ...settings, centerName: e.target.value })}
                  required
                  placeholder="e.g., DHI Sports Arena"
                  className="admin-input"
                  maxLength={100}
                />
              </div>
              <div className="admin-field" style={{ flex: 1 }}>
                <label className="admin-label">Primary Color</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="color"
                    value={settings.primaryColor}
                    onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                    style={{ width: '48px', height: '38px', padding: '2px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer' }}
                  />
                  <input
                    type="text"
                    value={settings.primaryColor}
                    onChange={(e) => setSettings({ ...settings, primaryColor: e.target.value })}
                    className="admin-input"
                    style={{ fontFamily: 'monospace' }}
                    maxLength={7}
                  />
                </div>
              </div>
            </div>

            {/* Tagline */}
            <div className="admin-field" style={{ marginBottom: '24px' }}>
              <label className="admin-label">Tagline / Slogan</label>
              <input
                type="text"
                value={settings.tagline}
                onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                placeholder="e.g., Your Premier Sports Destination"
                className="admin-input"
                maxLength={150}
              />
              <span style={{ fontSize: '11px', color: '#6b7280' }}>Optional - displayed below center name</span>
            </div>

            {/* UPI Payment Settings Section */}
            <div style={{
              marginBottom: '24px',
              padding: '20px',
              background: '#f0fdf4',
              borderRadius: '12px',
              border: '1px solid #bbf7d0'
            }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: '#166534', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                UPI Payment Settings
              </h4>
              <p style={{ fontSize: '13px', color: '#166534', margin: '0 0 16px 0', opacity: 0.8 }}>
                Configure UPI payment options to display on the public booking page for customers who want to pay online.
              </p>

              {/* UPI ID */}
              <div className="admin-field" style={{ marginBottom: '16px' }}>
                <label className="admin-label">UPI ID</label>
                <input
                  type="text"
                  value={settings.upiId}
                  onChange={(e) => setSettings({ ...settings, upiId: e.target.value })}
                  placeholder="e.g., yourname@upi or 9876543210@paytm"
                  className="admin-input"
                  maxLength={100}
                />
                <span style={{ fontSize: '11px', color: '#6b7280' }}>Your UPI ID for receiving payments (GPay, PhonePe, Paytm, etc.)</span>
              </div>

              {/* UPI QR Code URL */}
              <div className="admin-field">
                <label className="admin-label">UPI QR Code Image URL</label>
                <input
                  type="url"
                  value={settings.upiQrUrl}
                  onChange={(e) => setSettings({ ...settings, upiQrUrl: e.target.value })}
                  placeholder="https://example.com/your-upi-qr.png"
                  className="admin-input"
                />
                <span style={{ fontSize: '11px', color: '#6b7280' }}>
                  Upload your UPI QR code to an image hosting service and paste the URL here.
                  Customers can scan this to pay directly.
                </span>
              </div>

              {/* QR Preview */}
              {settings.upiQrUrl && (
                <div style={{ marginTop: '16px', textAlign: 'center' }}>
                  <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>QR Code Preview:</p>
                  <img
                    src={settings.upiQrUrl}
                    alt="UPI QR Code"
                    style={{
                      maxWidth: '150px',
                      maxHeight: '150px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      background: 'white',
                      padding: '8px'
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>

            {/* Live Preview */}
            <div style={{ marginBottom: '24px' }}>
              <label className="admin-label">Preview</label>
              <div style={{
                marginTop: '8px',
                padding: '20px',
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                borderRadius: '12px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Logo"
                      style={{ width: '60px', height: '60px', objectFit: 'contain', borderRadius: '8px' }}
                    />
                  ) : (
                    <div style={{
                      width: '60px',
                      height: '60px',
                      background: settings.primaryColor,
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '24px',
                      fontWeight: 'bold'
                    }}>
                      {settings.centerName ? settings.centerName.charAt(0).toUpperCase() : 'S'}
                    </div>
                  )}
                  <div>
                    <h3 style={{
                      margin: 0,
                      fontSize: '20px',
                      fontWeight: 700,
                      color: settings.primaryColor
                    }}>
                      {settings.centerName || 'Sports Center Name'}
                    </h3>
                    {settings.tagline && (
                      <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#64748b' }}>
                        {settings.tagline}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '8px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <button
                type="button"
                onClick={handleReset}
                disabled={saving}
                className="admin-btn admin-btn-secondary"
              >
                Reset
              </button>
              <button
                type="submit"
                disabled={saving}
                className="admin-btn admin-btn-primary"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Usage Instructions */}
      <div className="admin-card" style={{ marginTop: '16px' }}>
        <div className="admin-card-header">How Branding is Used</div>
        <div className="admin-card-body">
          <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#4b5563', lineHeight: 1.8 }}>
            <li>The <strong>Sports Center Name</strong> appears in the header of all pages</li>
            <li>The <strong>Logo</strong> is displayed in the navigation and booking confirmation</li>
            <li>The <strong>Primary Color</strong> is used for buttons and accent elements</li>
            <li>The <strong>Tagline</strong> appears on the customer booking page</li>
            <li>The <strong>UPI ID</strong> and <strong>QR Code</strong> are shown on the public booking page for online payments</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminBrandingSettings;
